import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";

/**
 * SearchableSelect — a lightweight searchable single-select dropdown.
 *
 * Props:
 *   value:        currently selected string (or "")
 *   onChange:     (newValue: string) => void
 *   options:      array of strings, or array of { value, label } objects
 *   placeholder:  placeholder text when no value
 *   allowClear:   show an "x" to clear the value (default true)
 *   disabled:     boolean
 *   testid:       data-testid on the trigger
 *   className:    extra classes on the trigger
 *   width:        Tailwind width class for the popover (default "w-[var(--radix-popover-trigger-width)]")
 *
 * Behaviour:
 *   - Type to filter the list.
 *   - Click an option to select.
 *   - Enter selects the first filtered match.
 *   - Escape closes.
 *   - Legacy values not in the list still render with a "(legacy)" suffix and can be re-selected.
 */
export function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  allowClear = true,
  disabled = false,
  testid,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  const normalized = useMemo(
    () =>
      (options || []).map((o) =>
        typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label || o.value }
      ),
    [options]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return normalized;
    return normalized.filter((o) => o.label.toLowerCase().includes(needle));
  }, [normalized, q]);

  const currentLabel = useMemo(() => {
    if (!value) return "";
    const hit = normalized.find((o) => o.value === value);
    if (hit) return hit.label;
    return `${value} (legacy)`;
  }, [normalized, value]);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  function commit(v) {
    onChange(v);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm text-left shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        >
          <span className={`truncate ${value ? "" : "text-muted-foreground"}`}>
            {value ? currentLabel : placeholder}
          </span>
          <span className="flex items-center gap-1 shrink-0 ml-2">
            {allowClear && value && !disabled && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear"
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onChange(""); } }}
                className="opacity-60 hover:opacity-100"
                data-testid={testid ? `${testid}-clear` : undefined}
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronsUpDown className="w-3.5 h-3.5 opacity-60" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] max-h-[320px] overflow-hidden flex flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="relative border-b">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && filtered[0]) commit(filtered[0].value);
              if (e.key === "Escape") setOpen(false);
            }}
            placeholder="Search…"
            className="h-9 pl-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <ul className="overflow-auto py-1" role="listbox">
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-muted-foreground">No matches.</li>
          )}
          {filtered.map((o) => {
            const selected = o.value === value;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={selected}
                onClick={() => commit(o.value)}
                className={`px-3 py-1.5 text-sm cursor-pointer flex items-center gap-2 hover:bg-secondary/60 ${selected ? "bg-secondary/40 font-medium" : ""}`}
              >
                <Check className={`w-3.5 h-3.5 ${selected ? "opacity-100" : "opacity-0"}`} />
                <span className="truncate">{o.label}</span>
              </li>
            );
          })}
          {value && !normalized.some((o) => o.value === value) && (
            <li
              role="option"
              aria-selected
              className="px-3 py-1.5 text-sm bg-secondary/40 italic text-muted-foreground"
            >
              <Check className="w-3.5 h-3.5 inline mr-2" />
              {value} (legacy)
            </li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/**
 * OtherField — render a conditional explanation/description field when
 * `triggerValue` matches "Other" (case-insensitive) or any custom triggers.
 *
 * Props:
 *   triggerValue:    current value of the dropdown that may equal "Other"
 *   value:           the other-text value
 *   onChange:        (newText: string) => void
 *   label:           label shown above the explanation field
 *   placeholder:     placeholder text
 *   testid:          data-testid on the input
 *   triggers:        list of strings considered "Other" (default ["Other"])
 *   asTextarea:      render a Textarea (false = single-line Input)
 */
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export function OtherField({
  triggerValue,
  value,
  onChange,
  label = "Please describe",
  placeholder = "Add a short explanation…",
  testid,
  triggers = ["Other"],
  asTextarea = false,
}) {
  const triggered = !!triggerValue && triggers.some((t) => String(t).toLowerCase() === String(triggerValue).toLowerCase());
  if (!triggered) return null;
  return (
    <div className="mt-2" data-testid={testid ? `${testid}-wrapper` : undefined}>
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {asTextarea ? (
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className="mt-1"
          data-testid={testid}
        />
      ) : (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-1"
          data-testid={testid}
        />
      )}
    </div>
  );
}

/**
 * SearchWithCategory — a search input paired with a category selector so users
 * always know what they are searching by.
 *
 * Props:
 *   query, onQueryChange
 *   category, onCategoryChange
 *   categories: [{ id, label }, ...]
 *   placeholder
 *   testidPrefix: e.g. "ready" -> data-testid="ready-search-input" / "ready-search-category"
 */
export function SearchWithCategory({
  query,
  onQueryChange,
  category,
  onCategoryChange,
  categories = [],
  placeholder = "Search…",
  testidPrefix = "search",
  className = "",
}) {
  return (
    <div className={`flex flex-col sm:flex-row gap-2 ${className}`}>
      <div className="sm:w-[200px]">
        <SearchableSelect
          value={category}
          onChange={onCategoryChange}
          options={categories.map((c) => ({ value: c.id, label: c.label }))}
          placeholder="Any field"
          allowClear={false}
          testid={`${testidPrefix}-search-category`}
        />
      </div>
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={query || ""}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={placeholder}
          className="pl-9"
          data-testid={`${testidPrefix}-search-input`}
        />
        {query && (
          <button
            type="button"
            onClick={() => onQueryChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
            data-testid={`${testidPrefix}-search-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Helper: apply a search query + category against an array of records.
 * - resolver(record, categoryId) -> string to compare (or null to skip)
 * - "all" / "any" categories perform a multi-field OR scan via knownFields
 */
export function matchesSearch(record, query, category, fieldResolver, anyFields = []) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  if (!category || category === "all" || category === "any") {
    for (const field of anyFields) {
      const v = fieldResolver(record, field);
      if (v != null && String(v).toLowerCase().includes(q)) return true;
    }
    return false;
  }
  const v = fieldResolver(record, category);
  return v != null && String(v).toLowerCase().includes(q);
}
